import React from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import AnimeGrid from '../../components/AnimeGrid/AnimeGrid';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import styles from './Home.module.css';

const fetcher = (...args) => fetch(...args).then(res => res.json());

const Home = () => {
  const { data: trending, error: trendingError, isLoading: trendingLoading } = 
    useSWR('/api/anime/trending', fetcher);
  
  const { data: popular, error: popularError, isLoading: popularLoading } = 
    useSWR('/api/anime/popular', fetcher);

  // Featured anime (would be selected from trending)
  const featuredAnime = trending?.data?.[0] || null;

  return (
    <div className={styles.container}>
      {/* Hero Section with Featured Anime */}
      {featuredAnime ? (
        <section 
          className={styles.hero}
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(11, 8, 40, 0.7), rgba(48, 43, 99, 0.9)), 
                             url(${featuredAnime.banner_image || featuredAnime.cover_image})`
          }}
        >
          <div className={styles.heroContent}>
            <div className={styles.heroInfo}>
              <h1 className={styles.heroTitle}>{featuredAnime.title_english || featuredAnime.title}</h1>
              <p className={styles.heroDescription}>
                {featuredAnime.description 
                  ? featuredAnime.description.slice(0, 180) + '...' 
                  : 'Explore the latest trending anime and discover new favorites.'}
              </p>
              <div className={styles.heroStats}>
                {featuredAnime.format && (
                  <span className={styles.heroStat}>{featuredAnime.format}</span>
                )}
                {featuredAnime.season && featuredAnime.season_year && (
                  <span className={styles.heroStat}>
                    {featuredAnime.season.charAt(0) + featuredAnime.season.slice(1).toLowerCase()} {featuredAnime.season_year}
                  </span>
                )}
                {featuredAnime.episodes > 0 && (
                  <span className={styles.heroStat}>{featuredAnime.episodes} Episodes</span>
                )}
              </div>
              <Link to={`/anime/${featuredAnime.id}`} className={styles.heroButton}>
                View Details
              </Link>
            </div>
            <div className={styles.heroImageContainer}>
              <img 
                src={featuredAnime.cover_image} 
                alt={featuredAnime.title} 
                className={styles.heroImage}
              />
            </div>
          </div>
        </section>
      ) : trendingLoading ? (
        <section className={styles.heroLoading}>
          <LoadingSpinner />
        </section>
      ) : (
        <section className={styles.heroFallback}>
          <div className={styles.heroContent}>
            <div className={styles.heroInfo}>
              <h1 className={styles.heroTitle}>Discover Anime</h1>
              <p className={styles.heroDescription}>
                Track, watch, and explore your favorite anime all in one place.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Main Content Sections */}
      <div className={styles.content}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Trending Now</h2>
          {trendingLoading ? (
            <LoadingSpinner />
          ) : trendingError ? (
            <ErrorMessage message="Failed to load trending anime" />
          ) : (
            <AnimeGrid animeList={trending?.data || []} viewAllLink="/trending" />
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Popular Anime</h2>
          {popularLoading ? (
            <LoadingSpinner />
          ) : popularError ? (
            <ErrorMessage message="Failed to load popular anime" />
          ) : (
            <AnimeGrid animeList={popular?.data || []} viewAllLink="/popular" />
          )}
        </section>

        <section className={styles.exploreSection}>
          <div className={styles.exploreBanner}>
            <div className={styles.exploreContent}>
              <h2 className={styles.exploreTitle}>Explore Anime by Genre</h2>
              <p className={styles.exploreDescription}>
                Find your next favorite anime by browsing through different genres
              </p>
              <Link to="/genres" className={styles.exploreButton}>
                Browse Genres
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home; 